<template>
  <v-stepper-vertical-item
    :value="stepValue"
    :elevation="0"
    :class="{ 'step-completed': currentStep > stepValue }"
  >
    <template #title>
      <div class="subtitle1">{{ $t("modals.allowCloudService.stepDownloadApp.title") }}</div>
    </template>
    <template #icon>
      <v-icon v-if="currentStep > stepValue" icon="$check" />
      <span v-else>{{ stepNumber }}</span>
    </template>
    <div>
      <p class="body2">{{ $t("modals.allowCloudService.stepDownloadApp.intro") }}</p>
      <p class="body2 font-weight-bold mt-4">
        {{ $t("modals.allowCloudService.stepDownloadApp.howToUse") }}
      </p>
      <ol class="body2 ml-6 mt-2">
        <li>{{ $t("modals.allowCloudService.stepDownloadApp.step1") }}</li>
        <li>{{ $t("modals.allowCloudService.stepDownloadApp.step2") }}</li>
        <li>{{ $t("modals.allowCloudService.stepDownloadApp.step3") }}</li>
      </ol>
      <div class="mt-5 d-flex align-center">
        <a
          class="coro-link body2"
          :class="{ disabled: disable || !appLink }"
          :href="appLink ?? undefined"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t("modals.allowCloudService.stepDownloadApp.downloadLink") }}
          <v-icon icon="$externalLink" size="14" />
        </a>
      </div>
      <div class="mt-4 d-flex">
        <v-btn
          color="primary"
          rounded
          :loading="loading"
          :disabled="disable"
          @click="moveToNextStep"
        >
          {{ $t("modals.allowCloudService.stepDownloadApp.actionBtn") }}
        </v-btn>
        <v-btn class="ml-4" @click="goBack">{{ $t("general.back") }}</v-btn>
      </div>
    </div>
  </v-stepper-vertical-item>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref } from "vue";
import { Service } from "@/constants/cloud-apps.ts";
import {
  SatConfigurationStatus,
  SatServiceProvider,
  SatServiceProviderToServiceMap,
} from "@/constants/security-awareness.ts";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";

export default defineComponent({
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    stepValue: {
      type: Number,
      required: true,
    },
    stepNumber: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Object as PropType<SatConnectionConfigForm>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["next", "back"],
  setup(props, { emit }) {
    const securityAwarenessStore = useSecurityAwarenessModule();
    const loading = ref(false);
    const appLink = ref<string | null>(null);

    const satConfigurationProvider = computed(
      () =>
        SatServiceProviderToServiceMap[props.modelValue.selectedService as Service] ??
        SatServiceProvider.CUSTOM
    );

    const moveToNextStep = async () => {
      loading.value = true;
      await securityAwarenessStore.updateSatConfig(
        { status: SatConfigurationStatus.DOWNLOAD_APP_STEP_COMPLETED },
        satConfigurationProvider.value
      );
      loading.value = false;
      emit("next");
    };

    const goBack = () => {
      emit("back");
    };

    onMounted(async () => {
      appLink.value = await securityAwarenessStore.getAllowlistAppLink();
    });

    return {
      loading,
      appLink,
      moveToNextStep,
      goBack,
    };
  },
});
</script>

<style scoped lang="scss">
.icon-external-link-xs:before {
  color: rgb(var(--v-theme-link)) !important;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding-top: 0;
}
</style>
